import { useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * SocraticDebateEngine Component
 * For advanced levels (Master/Ordination) - AI challenges student comprehension
 */
export function SocraticDebateEngine({
  lessonTopic,
  lessonContent,
  gradeLevel,
  persona,
  onDebateComplete
}) {
  const [debateMode, setDebateMode] = useState(false);
  const [debateHistory, setDebateHistory] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userResponse, setUserResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [feedback, setFeedback] = useState(null);
  
  // Check if grade level supports Socratic debate
  const isAdvancedLevel = ["Master", "Ordination", "College"].includes(gradeLevel);
  
  // Start Socratic debate session
  const startDebate = useCallback(async () => {
    setDebateMode(true);
    setDebateHistory([]);
    setScore({ correct: 0, total: 0 });
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/socratic/start`,
        {
          topic: lessonTopic,
          content: lessonContent?.substring(0, 1000),
          grade_level: gradeLevel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentQuestion({
        id: 1,
        question: response.data.question,
        hint: response.data.hint,
        scripture: response.data.scripture_reference
      });
      
    } catch (err) {
      console.error("Debate start error:", err);
      setDebateMode(false);
    } finally {
      setIsLoading(false);
    }
  }, [lessonTopic, lessonContent, gradeLevel]);
  
  // Submit response and get AI evaluation + follow-up
  const submitResponse = useCallback(async () => {
    if (!userResponse.trim() || !currentQuestion) return;
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/socratic/respond`,
        {
          question_id: currentQuestion.id,
          question: currentQuestion.question,
          user_response: userResponse,
          topic: lessonTopic,
          grade_level: gradeLevel,
          debate_history: debateHistory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add to history
      const newEntry = {
        question: currentQuestion.question,
        userResponse: userResponse,
        evaluation: response.data.evaluation,
        isCorrect: response.data.is_satisfactory,
        scripture: response.data.scripture_reference
      };
      
      setDebateHistory(prev => [...prev, newEntry]);
      setFeedback(response.data);
      
      // Update score
      setScore(prev => ({
        correct: prev.correct + (response.data.is_satisfactory ? 1 : 0),
        total: prev.total + 1
      }));
      
      // Set next question or end debate
      if (response.data.follow_up_question && score.total < 4) {
        setCurrentQuestion({
          id: currentQuestion.id + 1,
          question: response.data.follow_up_question,
          hint: response.data.hint,
          scripture: response.data.scripture_reference
        });
      } else {
        // End debate after 5 questions or no follow-up
        setCurrentQuestion(null);
        if (onDebateComplete) {
          onDebateComplete(score);
        }
      }
      
      setUserResponse("");
      
    } catch (err) {
      console.error("Response submit error:", err);
    } finally {
      setIsLoading(false);
      setFeedback(null);
    }
  }, [userResponse, currentQuestion, lessonTopic, gradeLevel, debateHistory, score, onDebateComplete]);
  
  // End debate early
  const endDebate = () => {
    setDebateMode(false);
    setCurrentQuestion(null);
    if (onDebateComplete) {
      onDebateComplete(score);
    }
  };
  
  if (!isAdvancedLevel) {
    return null; // Only show for advanced levels
  }
  
  return (
    <div style={{ marginTop: "20px" }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "15px"
      }}>
        <h4 style={{ 
          margin: 0, 
          color: persona?.color || "#FFD700",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          ⚔️ Socratic Challenge
        </h4>
        
        {debateMode && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}>
            <span style={{
              color: "#90EE90",
              fontSize: "0.8rem",
              background: "#1a3a1a",
              padding: "4px 10px",
              borderRadius: "10px"
            }}>
              Score: {score.correct}/{score.total}
            </span>
            <button
              onClick={endDebate}
              style={{
                background: "#333",
                color: "#ff6b6b",
                border: "none",
                borderRadius: "15px",
                padding: "4px 12px",
                cursor: "pointer",
                fontSize: "0.8rem"
              }}
            >
              End Session
            </button>
          </div>
        )}
      </div>
      
      {/* Start Button */}
      {!debateMode && (
        <div style={{
          background: "#1a1a2a",
          borderRadius: "12px",
          padding: "20px",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>🎓</div>
          <h5 style={{ color: "#FFD700", marginBottom: "10px" }}>
            Test Your Understanding
          </h5>
          <p style={{ color: "#aaa", fontSize: "0.9rem", marginBottom: "20px" }}>
            {persona?.name || "The AI"} will challenge your comprehension through 
            Socratic questioning. This is for {gradeLevel} level learners.
          </p>
          <button
            onClick={startDebate}
            disabled={isLoading}
            style={{
              background: persona?.bgGradient || "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              color: "#000",
              border: "none",
              borderRadius: "25px",
              padding: "12px 30px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "1rem"
            }}
          >
            {isLoading ? "Preparing..." : "Begin Challenge"}
          </button>
        </div>
      )}
      
      {/* Active Debate */}
      {debateMode && currentQuestion && (
        <div style={{
          background: "#1a1a2a",
          borderRadius: "12px",
          padding: "20px",
          border: `2px solid ${persona?.color || "#FFD700"}`
        }}>
          {/* Question */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px"
            }}>
              <div style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: persona?.bgGradient || "#FFD700",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
                flexShrink: 0
              }}>
                {persona?.avatar || "🎓"}
              </div>
              <div>
                <p style={{ 
                  color: "#fff", 
                  fontSize: "1rem", 
                  lineHeight: "1.6",
                  margin: "0 0 10px 0"
                }}>
                  {currentQuestion.question}
                </p>
                
                {currentQuestion.scripture && (
                  <p style={{
                    color: persona?.color || "#FFD700",
                    fontSize: "0.85rem",
                    fontStyle: "italic",
                    margin: 0
                  }}>
                    📖 {currentQuestion.scripture}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Hint (collapsible) */}
          {currentQuestion.hint && (
            <details style={{ marginBottom: "15px" }}>
              <summary style={{
                color: "#aaa",
                cursor: "pointer",
                fontSize: "0.85rem"
              }}>
                💡 Need a hint?
              </summary>
              <p style={{
                color: "#90EE90",
                fontSize: "0.85rem",
                marginTop: "8px",
                paddingLeft: "15px",
                borderLeft: "2px solid #90EE90"
              }}>
                {currentQuestion.hint}
              </p>
            </details>
          )}
          
          {/* Response Input */}
          <div style={{ marginBottom: "15px" }}>
            <textarea
              value={userResponse}
              onChange={(e) => setUserResponse(e.target.value)}
              placeholder="Share your understanding..."
              disabled={isLoading}
              style={{
                width: "100%",
                background: "#0a0a0a",
                border: "1px solid #333",
                borderRadius: "10px",
                padding: "12px",
                color: "#fff",
                fontSize: "0.95rem",
                resize: "none",
                minHeight: "100px",
                fontFamily: "inherit"
              }}
            />
          </div>
          
          {/* Submit Button */}
          <button
            onClick={submitResponse}
            disabled={isLoading || !userResponse.trim()}
            style={{
              background: isLoading || !userResponse.trim() 
                ? "#333" 
                : (persona?.bgGradient || "#FFD700"),
              color: isLoading || !userResponse.trim() ? "#666" : "#000",
              border: "none",
              borderRadius: "20px",
              padding: "10px 25px",
              cursor: isLoading || !userResponse.trim() ? "not-allowed" : "pointer",
              fontWeight: "bold",
              width: "100%"
            }}
          >
            {isLoading ? "Evaluating..." : "Submit Response"}
          </button>
        </div>
      )}
      
      {/* Debate Complete */}
      {debateMode && !currentQuestion && score.total > 0 && (
        <div style={{
          background: "#1a1a2a",
          borderRadius: "12px",
          padding: "25px",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "3rem", marginBottom: "15px" }}>
            {score.correct >= score.total * 0.7 ? "🏆" : score.correct >= score.total * 0.5 ? "📚" : "💪"}
          </div>
          <h4 style={{ color: "#FFD700", marginBottom: "10px" }}>
            Challenge Complete!
          </h4>
          <p style={{ 
            color: "#fff", 
            fontSize: "1.5rem", 
            fontWeight: "bold",
            marginBottom: "10px"
          }}>
            {score.correct} / {score.total}
          </p>
          <p style={{ color: "#aaa", fontSize: "0.9rem", marginBottom: "20px" }}>
            {score.correct >= score.total * 0.7 
              ? "Excellent comprehension! You've demonstrated deep understanding."
              : score.correct >= score.total * 0.5 
                ? "Good effort! Review the material and try again."
                : "Keep studying! The path to wisdom requires persistence."}
          </p>
          <button
            onClick={() => {
              setDebateMode(false);
              setScore({ correct: 0, total: 0 });
            }}
            style={{
              background: "#333",
              color: "#fff",
              border: "none",
              borderRadius: "20px",
              padding: "10px 25px",
              cursor: "pointer"
            }}
          >
            Close
          </button>
        </div>
      )}
      
      {/* Debate History */}
      {debateHistory.length > 0 && currentQuestion && (
        <div style={{ marginTop: "20px" }}>
          <h5 style={{ color: "#aaa", marginBottom: "10px", fontSize: "0.85rem" }}>
            Previous Exchanges:
          </h5>
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {debateHistory.map((entry, idx) => (
              <div 
                key={idx}
                style={{
                  background: "#111",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "8px",
                  borderLeft: `3px solid ${entry.isCorrect ? "#90EE90" : "#ff6b6b"}`
                }}
              >
                <p style={{ color: "#aaa", fontSize: "0.8rem", margin: "0 0 5px 0" }}>
                  Q: {entry.question.substring(0, 100)}...
                </p>
                <p style={{ 
                  color: entry.isCorrect ? "#90EE90" : "#ff6b6b", 
                  fontSize: "0.75rem", 
                  margin: 0 
                }}>
                  {entry.isCorrect ? "✓ Satisfactory" : "✗ Needs improvement"}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SocraticDebateEngine;
